import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { atom, useAtom, useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import type {
    EmissionRecordsListRequest,
    EmissionRecordView,
    SiteNonDetect,
} from "../../../../../../apiClient/generated";
import {
    useEmissionRecordsApiClient,
    useNondetectsApiClient,
} from "../../../../../../hooks";
import { fetchAllWithPagination } from "../../../../../../utils/api";
import { formatDateTime } from "../../../../../../utils/dates";
import { emissionPageStateFamily, selectionContextAtom } from "../../../state";
import { computeAverageDetectedRate } from "./bridger";
import { Legend } from "./legend";
import { EmissionPlot } from "./plot";

interface SatelliteEmissionsProps {
    siteId: string;
}

const rateFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const logScaleAtom = atom(true);
const showIndividualEmissionsAtom = atom(false);

export const SatelliteEmissions = (props: SatelliteEmissionsProps) => {
    const { dateFrom, dateTo, emissionFilters } = useAtomValue(
        emissionPageStateFamily(props.siteId),
    );
    const siteFilters = useMemo(
        () => ({ dateFrom, dateTo }),
        [dateFrom, dateTo],
    );
    const [selectionContext] = useAtom(selectionContextAtom);
    const [logScale, setLogScale] = useAtom(logScaleAtom);
    const [showIndividualEmissions, setShowIndividualEmissions] = useAtom(
        showIndividualEmissionsAtom,
    );
    const [cursorPosition, setCursorPosition] = useState<{
        x: Date;
        y: number;
    }>();

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore = siteFilters.dateTo;
            if (selectionContext.dateRange?.end) {
                dateBefore = selectionContext.dateRange?.end;
            }
            let dateAfter = siteFilters.dateFrom;
            if (selectionContext.dateRange?.start) {
                dateAfter = selectionContext.dateRange.start;
            }
            return { dateBefore, dateAfter };
        }, [
            siteFilters.dateFrom,
            siteFilters.dateTo,
            selectionContext.dateRange,
        ]),
        100,
    );

    // Retrieve emission data
    const emissionRecordsApiClient = useEmissionRecordsApiClient();
    const emissionRecordsQuery = useQuery({
        queryKey: [
            "infrastructureEmissionRecords",
            props.siteId,
            siteFilters,
            emissionFilters,
        ],
        queryFn: async () => {
            const results = await fetchAllWithPagination<
                EmissionRecordsListRequest,
                EmissionRecordView
            >(
                emissionRecordsApiClient.emissionRecordsList.bind(
                    emissionRecordsApiClient,
                ),
                {
                    ...emissionFilters,
                    relatedSite: props.siteId,
                    detectionDateRangeAfter: siteFilters.dateFrom,
                    detectionDateRangeBefore: siteFilters.dateTo,
                    pageSize: 200,
                    providerWithSource: emissionFilters.providerWithSource
                        ? (JSON.stringify(
                              emissionFilters.providerWithSource,
                          ) as any)
                        : undefined,
                },
            );
            return results;
        },
        enabled: !!props.siteId,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        staleTime: 0,
    });

    const nondetectsApiClient = useNondetectsApiClient();
    const nondetectsQuery = useQuery({
        queryKey: ["infrastructureSiteNonDetects", props.siteId, siteFilters],
        queryFn: async (): Promise<SiteNonDetect[]> =>
            fetchAllWithPagination(
                nondetectsApiClient.nondetectsSitenondetectList.bind(
                    nondetectsApiClient,
                ),
                {
                    site: props.siteId,
                    timestampAfter: siteFilters.dateFrom,
                    timestampBefore: siteFilters.dateTo,
                    pageSize: 200,
                },
            ),
        enabled: !!props.siteId,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        staleTime: 0,
    });

    // Average of the site level observations currently in range, using the same
    // definition as the analytics per site averages plot.
    const averageRate = useMemo(() => {
        const records = (emissionRecordsQuery.data || []).filter(
            (d) =>
                d.dataPoint.detectionTimestamp >=
                    debouncedDateRange.dateAfter &&
                d.dataPoint.detectionTimestamp <= debouncedDateRange.dateBefore,
        );
        return computeAverageDetectedRate(records);
    }, [emissionRecordsQuery.data, debouncedDateRange]);

    return (
        <div className="flex h-72">
            <div className="p-1 h-full w-[200px]">
                <div className="my-2 flex flex-col gap-1">
                    <label className="flex items-center gap-1">
                        <input
                            type="checkbox"
                            className="rounded text-ae-blue-550"
                            checked={logScale}
                            onChange={() => setLogScale(!logScale)}
                        />
                        Log Scale
                    </label>
                    <label className="flex items-center gap-1">
                        <input
                            type="checkbox"
                            className="rounded text-ae-blue-550"
                            checked={showIndividualEmissions}
                            onChange={() =>
                                setShowIndividualEmissions(
                                    !showIndividualEmissions,
                                )
                            }
                        />
                        Show Individual Measurements
                    </label>
                </div>
                <Legend />
                <div className="mt-2 pt-2 text-xs text-ae-slate-600">
                    Average detected rate
                    <br />
                    <span className="text-ae-slate-900">
                        {averageRate !== undefined
                            ? `${rateFormatter.format(averageRate)} kg/h`
                            : "No detections in range"}
                    </span>
                </div>
                {cursorPosition && (
                    <div className="mt-2 text-xs text-ae-slate-600">
                        Cursor at:
                        <br />
                        Date: {formatDateTime(cursorPosition.x)}
                        <br />
                        Rate: {rateFormatter.format(cursorPosition.y)} kg/h
                    </div>
                )}
            </div>
            <div className="flex-1 relative">
                <EmissionPlot
                    dataDateRange={debouncedDateRange}
                    data={emissionRecordsQuery.data || []}
                    nonDetects={nondetectsQuery.data || []}
                    showIndividualEmissionsFromGroups={showIndividualEmissions}
                    logScale={logScale}
                    setCursorPosition={setCursorPosition}
                />
                {(emissionRecordsQuery.isFetching ||
                    nondetectsQuery.isFetching) && (
                    <div className="top-0 right-0 bg-neutral-400 bg-opacity-20 absolute w-full h-full flex items-center justify-center">
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin text-2xl"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
